import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthStatus, useAuthContext } from "../../hooks/useAuthContext";
import { useEffect, useRef, useState } from "react";
import MeaButton from "../../components/MeaButton";
import MeaLink from "../../components/MeaLink";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import ReCAPTCHA from "react-google-recaptcha";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import authAPI from "../../services/auth";

/**
 * Reusable login form card.
 * showBackButton — show a back arrow (used in standalone /auth/login page)
 */
export function LoginFormCard({ showBackButton = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get("error");
  const emailParam = searchParams.get("email");

  const from = location.state?.from?.pathname || "/school";
  const { login, loginWithCode, status } = useAuthContext();
  
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loginMode, setLoginMode] = useState("code"); // "code" or "password"
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [captchaRequired, setCaptchaRequired] = useState(false);
  
  const [authConfig, setAuthConfig] = useState({
    oauth_login_enabled: false,
    google_login_enabled: false,
  });

  const captchaRef = useRef(null);
  const toastRef = useRef(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    if (status === AuthStatus.AUTHENTICATED) {
      navigate(from, { replace: true });
    }
  }, [status, from, navigate]);

  useEffect(() => {
    authAPI.getConfig()
      .then((res) => {
        setAuthConfig(res);
      })
      .catch((err) => {
        console.error("Failed to load auth config", err);
      });
  }, []);

  const [ssoError, setSsoError] = useState("");

  useEffect(() => {
    if (errorParam) {
      let msg = "Une erreur d'authentification SSO est survenue.";
      if (errorParam === "account_not_found") {
        msg = `Aucun compte n'est associé à l'adresse e-mail "${emailParam}". Veuillez utiliser l'adresse e-mail enregistrée dans votre école ou contacter votre administrateur.`;
      } else if (errorParam === "oauth_disabled") {
        msg = "La connexion SSO/OAuth est désactivée sur la plateforme.";
      } else if (errorParam === "account_blocked") {
        msg = "Ce compte a été bloqué par l'administrateur.";
      } else if (errorParam === "invalid_provider") {
        msg = "Le fournisseur d'authentification SSO est invalide.";
      } else if (errorParam === "oauth_not_configured") {
        msg = "Le service d'authentification SSO n'est pas configuré sur le serveur.";
      }
      setSsoError(msg);
    } else {
      setSsoError("");
    }
  }, [errorParam, emailParam]);

  const handleSSO = (provider) => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/v2/auth/oauth/login/${provider}`;
  };

  const handleRequestCode = () => {
    if (!username) {
      toastRef.current.show({
        severity: "warn",
        summary: "Email requis",
        detail: "Veuillez saisir votre adresse email.",
        life: 5000,
      });
      return;
    }
    const captchaToken = captchaRef.current ? captchaRef.current.getValue() : null;
    setLoading(true);
    setErrMsg("");
    authAPI.requestCode(username, captchaToken)
      .then((res) => {
        setCodeSent(true);
        setCaptchaRequired(res?.captcha_required || false);
        if (captchaRef.current) captchaRef.current.reset();
        setToken(null);
        toastRef.current.show({
          severity: "success",
          summary: "Code envoyé",
          detail: "Un code d'authentification a été envoyé à votre adresse email.",
          life: 5000,
        });
        setLoading(false);
      })
      .catch((err) => {
        if (err.captcha_required) {
          setCaptchaRequired(true);
          setCodeSent(false);
        }
        toastRef.current.show({
          severity: "error",
          summary: "Erreur",
          detail: err.message || "Impossible d'envoyer le code d'authentification.",
          life: 5000,
        });
        setLoading(false);
        if (captchaRef.current) captchaRef.current.reset();
        setToken(null);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const captchaToken = captchaRef.current ? captchaRef.current.getValue() : null;
    setLoading(true);
    setErrMsg("");

    if (loginMode === "password") {
      login(username, password, "account_login", captchaToken)
        .then((result) => {
          if (result === true) {
            setUsername("");
            setPassword("");
            setCaptchaRequired(false);
          }
          setLoading(false);
        })
        .catch((error) => {
          if (error.captcha_required) {
            setCaptchaRequired(true);
          }
          toastRef.current.show({
            severity: "error",
            summary: "Erreur de connexion",
            detail: error.message || "Identifiant ou mot de passe incorrect",
            life: 5000,
          });
          setLoading(false);
          setPassword("");
          if (captchaRef.current) captchaRef.current.reset();
          setToken(null);
        });
    } else {
      // login with code
      loginWithCode(username, code, captchaToken)
        .then((result) => {
          if (result === true) {
            setUsername("");
            setCode("");
            setCodeSent(false);
            setCaptchaRequired(false);
          }
          setLoading(false);
        })
        .catch((error) => {
          toastRef.current.show({
            severity: "error",
            summary: "Erreur de connexion",
            detail: error.message || "Code d'authentification incorrect ou expiré",
            life: 5000,
          });
          setLoading(false);
          if (captchaRef.current) captchaRef.current.reset();
          setToken(null);
        });
    }
  };

  return (
    <>
      <Toast ref={toastRef} />
      <div style={{ maxWidth: "420px" }} className="surface-card p-4 shadow-2 border-round w-full">
        <div className="text-center mb-4">
          <div className="flex align-items-center justify-content-center gap-2 mb-3">
            {showBackButton && (
              <MeaButton
                icon="pi pi-arrow-left"
                text
                style={{ minHeight: "50px", maxWidth: "50px" }}
                onClick={() => navigate("/")}
              />
            )}
            <MeaLink to="/"><img src="/images/mealogo.png" alt="MEA" height={50} /></MeaLink>
          </div>
          <div className="text-900 text-3xl font-medium mb-2">Connexion</div>
        </div>

        {authConfig.oauth_login_enabled && !codeSent && (
          <div className="mb-4 text-center">
            <div className="text-600 text-sm mb-3">Vous utilisez un email Gmail ? Connectez-vous avec Google</div>
            <div className="flex justify-content-center">
              {authConfig.google_login_enabled && (
                <Button
                  type="button"
                  onClick={() => handleSSO("google")}
                  outlined
                  severity="secondary"
                  style={{ width: "160px", height: "50px" }}
                  className="flex justify-content-center align-items-center"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path fill="#EA4335" d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.354 0 3.393 2.673 1.411 6.573L5.266 9.765z"/>
                    <path fill="#34A853" d="M16.04 15.342c-1.047.747-2.4 1.176-4.04 1.176a7.07 7.07 0 0 1-6.734-4.856L1.411 14.83C3.393 18.73 7.354 21.4 12 21.4c3.155 0 5.864-1.037 7.79-2.827l-3.75-3.231z"/>
                    <path fill="#4285F4" d="M24 12c0-.773-.09-1.573-.255-2.345H12v4.69h6.758A5.918 5.918 0 0 1 16.04 15.342l3.75 3.231C22.01 16.545 24 14.527 24 12z"/>
                    <path fill="#FBBC05" d="M5.266 11.662a6.974 6.974 0 0 1 0-1.897L1.411 5.89A11.96 11.96 0 0 0 0 12c0 2.224.607 4.316 1.411 6.11l3.855-3.09a6.974 6.974 0 0 1 0-3.358z"/>
                  </svg>
                </Button>
              )}
            </div>

            <div className="flex align-items-center my-4">
              <div className="flex-grow-1 border-top-1 border-300"></div>
              <span className="px-3 text-500 font-medium text-sm">ou</span>
              <div className="flex-grow-1 border-top-1 border-300"></div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {ssoError && (
            <Message severity="error" text={ssoError} className="block w-full mb-3" />
          )}
          <label htmlFor="email" className="block text-900 font-medium mb-2">Adresse email</label>
          <InputText
            id="email"
            type="text"
            className="w-full mb-3"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={codeSent}
            autoFocus
          />

          {loginMode === "password" && (
            <>
              <label htmlFor="password" className="block text-900 font-medium mb-2">Mot de passe</label>
              <InputText
                id="password"
                type="password"
                className="w-full mb-3"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div className="flex align-items-center justify-content-end mb-4">
                <MeaLink to="/auth/resetpass">
                  <span className="font-medium no-underline text-blue-500 cursor-pointer text-sm">
                    Mot de passe oublié ?
                  </span>
                </MeaLink>
              </div>
            </>
          )}

          {loginMode === "code" && codeSent && (
            <>
              <label htmlFor="code" className="block text-900 font-medium mb-2">Code d'authentification</label>
              <InputText
                id="code"
                type="text"
                className="w-full mb-3"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Entrez le code à 6 chiffres"
                autoFocus
              />

              <div className="flex align-items-center justify-content-between mb-4">
                <span 
                  className="font-medium text-blue-500 cursor-pointer text-sm"
                  onClick={handleRequestCode}
                >
                  Renvoyer le code
                </span>
                <span 
                  className="font-medium text-blue-500 cursor-pointer text-sm"
                  onClick={() => { setCodeSent(false); setCode(""); }}
                >
                  Changer d'email
                </span>
              </div>
            </>
          )}

          {((loginMode === "password" || (loginMode === "code" && !codeSent)) && captchaRequired) && (
            <div className="flex align-items-center justify-content-between mb-4">
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                onChange={(value) => setToken(value)}
                ref={captchaRef}
              />
            </div>
          )}

          {errMsg && (
            <Message severity="error" text={errMsg} className="block w-full mb-3" />
          )}

          {loginMode === "code" && !codeSent ? (
            <MeaButton
              type="button"
              disabled={username === "" || (captchaRequired && token === null)}
              style={{ height: "50px" }}
              label="Envoyer le code d'authentification"
              icon="pi pi-send"
              className="w-full"
              loading={loading}
              onClick={handleRequestCode}
            />
          ) : (
            <MeaButton
              disabled={
                (loginMode === "password" && captchaRequired && token === null) || 
                username === "" || 
                (loginMode === "password" && password === "") || 
                (loginMode === "code" && code === "")
              }
              style={{ height: "50px" }}
              label="S'identifier"
              icon="pi pi-user"
              className="w-full"
              loading={loading}
            />
          )}
        </form>

        <div className="text-center mt-4">
          {loginMode === "code" ? (
            <span 
              className="font-medium text-purple-700 cursor-pointer text-sm hover:underline"
              onClick={() => {
                setLoginMode("password");
                setErrMsg("");
                setToken(null);
                setCaptchaRequired(false);
                if (captchaRef.current) captchaRef.current.reset();
              }}
            >
              Se connecter avec un mot de passe
            </span>
          ) : (
            <span 
              className="font-medium text-purple-700 cursor-pointer text-sm hover:underline"
              onClick={() => {
                setLoginMode("code");
                setErrMsg("");
                setToken(null);
                setCaptchaRequired(false);
                if (captchaRef.current) captchaRef.current.reset();
              }}
            >
              Se connecter sans mot de passe (via e-mail)
            </span>
          )}
        </div>
      </div>
    </>
  );
}
